import { ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { useMediaQuery } from '@givebutter/react-util';
import { createCampaign } from 'api/campaigns';
import { CampaignAddFeaturesSelection } from 'components/create-campaign-dialog/CampaignAddFeaturesSelection';
import { ComparisonList } from 'components/create-campaign-dialog/CampaignComparisonList';
import { CampaignSelectType } from 'components/create-campaign-dialog/CampaignSelectType';
import { CampaignSuccess } from 'components/create-campaign-dialog/CampaignSuccess';
import { CampaignTitleInput } from 'components/create-campaign-dialog/CampaignTitleInput';
import {
    CreateCampaignDialogForm,
    CreateCampaignDialogProvider,
    campaignTypePosthog,
    useCreateCampaignDialogContext,
} from 'components/create-campaign-dialog/CreateCampaignDialogContext';
import * as Dialog from 'components/dialog';
import * as Form from 'components/form';
import { useInvalidateChecklistQuery } from 'components/required-checklist/useChecklistData';
import { history } from 'components/router/history';
import { posthogEvents } from 'constants/posthog-events';
import { TestIds } from 'constants/tests';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { usePosthog } from 'customHooks/usePosthog';
import { AnimatePresence, motion } from 'framer-motion';
import { EventType } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

enum CreateCampaignDialogTriggerLocation {
    SidebarNavGlobalCreateNewItem = 'Sidebar Navigation - Global Create New Item',
    SidebarNavNewCampaign = 'Sidebar Navigation - New Campaign',
    CampaignsPage = 'Campaigns Page',
    CampaignsTable = 'Campaigns Table',
    OnboardingChecklist = 'Onboarding Checklist',
    RequiredChecklistDashboard = 'Required Checklist - Dashboard',
    RequiredChecklistFloating = 'Required Checklist - Floating',
    SearchBar = 'Search Bar',
    RecommendedActions = 'Recommended Actions',
}

interface CreateCampaignDialogProps extends Dialog.DialogProps {
    children?: ReactNode;
    triggerLocation: CreateCampaignDialogTriggerLocation;
    defaultType?: EventType;
}

const CreateCampaignDialog = ({
    children,
    triggerLocation,
    defaultType = 'collect',
}: CreateCampaignDialogProps) => {
    const toast = useToast();
    const { currentStep, setStep } = useCreateCampaignDialogContext();
    const { invalidateChecklistQuery } = useInvalidateChecklistQuery();
    const { accountId } = useCurrentAccount();
    const { error: errorToast } = useToast();
    const [isOpen, setIsOpen] = useState(false);
    const { pushEvent } = usePosthog();
    const isMobile = useMediaQuery(`(max-width: 640px)`);
    const methods = Form.useForm<CreateCampaignDialogForm>({
        defaultValues: {
            campaignName: '',
            type: defaultType,
            enableTeamFundraising: false,
            enableAuction: false,
        },
    });
    const { handleSubmit, watch, reset } = methods;

    const [eventType, campaignName, enableTeamFundraising, enableAuction] = watch([
        'type',
        'campaignName',
        'enableTeamFundraising',
        'enableAuction',
    ]);

    const payoffTimeout = useRef<NodeJS.Timeout>();

    const { mutate: createNewCampaign } = useMutation({
        mutationFn: (formData: CreateCampaignDialogForm) =>
            createCampaign(accountId, {
                type: formData.type,
                enable_team_fundraising: formData.enableTeamFundraising,
                enable_auction: formData.enableAuction,
                title: formData.campaignName,
            }),
        onSuccess: async ({ campaign }) => {
            pushEvent(posthogEvents.CAMPAIGN_CREATE_SUCCESS, {
                type: campaignTypePosthog[eventType],
                campaignName,
                enableTeamFundraising,
                enableAuction,
            });
            payoffTimeout.current = setTimeout(() => {
                setIsOpen(false);
                onClose();
                toast.success('Created campaign successfully!');
                history.push(`/accounts/${accountId}/campaigns/${campaign.id}/details`);
            }, 2000);
        },
        onError: (error: GivebutterAxiosError) => {
            const apiErrorMsg =
                error.response?.data.message ?? 'Failed to create campaign. Please try again.';
            pushEvent(posthogEvents.CAMPAIGN_CREATE_FAILED, {
                type: campaignTypePosthog[eventType],
                campaignName,
                enableTeamFundraising,
                enableAuction,
                error: apiErrorMsg,
            });
            errorToast(apiErrorMsg);
            setIsOpen(false);
            onClose();
        },
    });

    const onClose = useCallback(() => {
        reset();
        setStep('start');
        payoffTimeout.current && clearTimeout(payoffTimeout.current);
        invalidateChecklistQuery();
    }, [reset, setStep, invalidateChecklistQuery]);

    const onSubmit = useCallback(
        (data: CreateCampaignDialogForm) => {
            createNewCampaign(data);
            pushEvent(posthogEvents.CAMPAIGN_CREATE_SUBMITTED, {
                ...data,
                type: campaignTypePosthog[data.type],
            });
            setStep('success');
        },
        [createNewCampaign, pushEvent, setStep],
    );

    const currentStepComponent = useMemo(() => {
        switch (currentStep) {
            case 'start':
                return <CampaignSelectType />;
            case 'info':
                return <ComparisonList />;
            case 'addFeatures':
                return <CampaignAddFeaturesSelection />;
            case 'newTitle':
                return <CampaignTitleInput />;
            case 'success':
                return <CampaignSuccess />;
            default:
                return null;
        }
    }, [currentStep]);

    return (
        <Form.Provider {...methods}>
            <Dialog.Root
                open={isOpen}
                onOpenChange={open => {
                    if (!open) {
                        onClose();
                    } else {
                        pushEvent(posthogEvents.CAMPAIGN_CREATE_STARTED, {
                            location: triggerLocation,
                        });
                    }

                    setIsOpen(open);
                }}
            >
                {children}
                <Dialog.Portal>
                    <Dialog.Overlay />
                    <Dialog.Title>Create a Campaign</Dialog.Title>
                    <Dialog.Content
                        className={
                            'm-0 block h-dvh max-h-none w-dvw max-w-none rounded-none p-0 sm:rounded-xl md:max-h-[664px] md:max-w-[808px] lg:max-h-[664px] lg:max-w-5xl'
                        }
                    >
                        {(isMobile || (currentStep !== 'info' && currentStep !== 'success')) && (
                            <Dialog.Close
                                data-testid={TestIds.Campaigns.DialogClose}
                                className="right-4 size-10 rounded-full bg-gray-100 md:left-4"
                            />
                        )}
                        <form className="h-full w-full" onSubmit={handleSubmit(onSubmit)}>
                            <Dialog.Body className="h-full w-full">
                                <AnimatePresence initial={false} mode="wait">
                                    <motion.div
                                        key={currentStep}
                                        className="h-full w-full"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        {currentStepComponent}
                                    </motion.div>
                                </AnimatePresence>
                            </Dialog.Body>
                        </form>
                    </Dialog.Content>
                </Dialog.Portal>
            </Dialog.Root>
        </Form.Provider>
    );
};

const CreateCampaignDialogWrapper = (props: CreateCampaignDialogProps) => {
    return (
        <CreateCampaignDialogProvider>
            <CreateCampaignDialog {...props} />
        </CreateCampaignDialogProvider>
    );
};

const CreateCampaignDialogTrigger = Dialog.Trigger;

const Root = CreateCampaignDialogWrapper;
const Trigger = CreateCampaignDialogTrigger;
const TriggerLocation = CreateCampaignDialogTriggerLocation;

export {
    CreateCampaignDialogWrapper as CreateCampaignDialog,
    CreateCampaignDialogTrigger,
    CreateCampaignDialogTriggerLocation,
    //
    Root,
    Trigger,
    TriggerLocation,
};
export type { CreateCampaignDialogProps };
